import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Settings as SettingsIcon, Building2, Save } from 'lucide-react';
import { api } from '@weebee/api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { requireLevel } from '@/lib/guards';
import { useCompany } from '@/lib/company-context';
import { Skeleton } from '@/components/shared/skeleton';
import { ErrorState } from '@/components/shared/error-state';

export const Route = createFileRoute('/app/settings')({
  beforeLoad: requireLevel(7),
  component: SettingsPage,
});

function SettingsPage() {
  const { company } = useCompany();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['corporate', company.slug, 'detail'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/corporate/companies/{slug}', {
        params: { path: { slug: company.slug } },
      });
      if (error) throw error;
      return data;
    },
  });

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <SettingsIcon className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-xl font-bold text-foreground">Company Settings</h1>
      </div>

      {isLoading ? (
        <div className="rounded-xl border border-border bg-card p-6 space-y-4">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : data ? (
        <CompanyProfileForm
          slug={company.slug}
          initialName={data.name ?? ''}
          initialDisplayName={data.display_name ?? ''}
          initialIndustry={data.industry ?? ''}
        />
      ) : null}
    </div>
  );
}

interface CompanyProfileFormProps {
  slug: string;
  initialName: string;
  initialDisplayName: string;
  initialIndustry: string;
}

function CompanyProfileForm({
  slug,
  initialName,
  initialDisplayName,
  initialIndustry,
}: CompanyProfileFormProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(initialName);
  const [displayName, setDisplayName] = useState(initialDisplayName);
  const [industry, setIndustry] = useState(initialIndustry);
  const [success, setSuccess] = useState(false);

  const mutation = useMutation({
    mutationFn: async () => {
      const { error } = await api.PATCH('/api/v1/corporate/companies/{slug}', {
        params: { path: { slug } },
        body: { name, display_name: displayName, industry },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['corporate', slug] });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    },
  });

  const hasChanges =
    name !== initialName || displayName !== initialDisplayName || industry !== initialIndustry;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
      className="rounded-xl border border-border bg-card p-6 space-y-4"
    >
      <div className="flex items-center gap-2 mb-2">
        <Building2 className="h-5 w-5 text-muted-foreground" />
        <h2 className="text-base font-semibold text-foreground">Company Profile</h2>
      </div>

      <div className="space-y-1.5">
        <label htmlFor="name" className="text-sm font-medium text-foreground">
          Name
        </label>
        <input
          id="name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      <div className="space-y-1.5">
        <label htmlFor="displayName" className="text-sm font-medium text-foreground">
          Display Name
        </label>
        <input
          id="displayName"
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      <div className="space-y-1.5">
        <label htmlFor="industry" className="text-sm font-medium text-foreground">
          Industry
        </label>
        <input
          id="industry"
          type="text"
          value={industry}
          onChange={(e) => setIndustry(e.target.value)}
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {mutation.isError && (
        <p className="text-sm text-destructive">Failed to save. Please try again.</p>
      )}
      {success && (
        <p className="text-sm text-green-600 dark:text-green-400">Settings saved successfully.</p>
      )}

      <button
        type="submit"
        disabled={!hasChanges || mutation.isPending}
        className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
      >
        <Save className="h-4 w-4" />
        {mutation.isPending ? 'Saving...' : 'Save Changes'}
      </button>
    </form>
  );
}
